import { DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Avatar, Button, Carousel, Dropdown, Image, MenuProps, Popover } from 'antd';
import fallbackHolder from 'assets/img/fallback.png';
import cn from 'classnames';
import Spinner from 'components/Spinner/Spinner';
import { checkUserMentioned, parsedChatMessages } from 'helpers/getLinkedTextArray';
import { TFunction } from 'i18next';
import { IChatMessage } from 'interfaces';
import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import fetchChatData from 'store/reducers/chatCreator';
import { setAttemptCount, setChatMessage, setChatStatus, setCheckingMessages } from 'store/slices/chatsSlice';
import classes from './Messages.module.scss';

type statusesType = {
  popupContent?: JSX.Element;
};

const statusCases = {
  sent: () => (
    <div className={classes.checked}>
      <div className={classes.check}></div>
    </div>
  ),
  pending: () => <div className={classes.checked}>
    <Spinner size={14} color='white' width='fit-content' />
  </div>,
  viewed: () => (
    <div className={cn(classes.checked, classes.viewed)}>
      <div className={classes.check}></div>
      <div className={classes.check}></div>
    </div>
  ),
  rejected: ({ popupContent }: statusesType) => (
    <Popover content={popupContent} placement='bottomRight' trigger='click'>
      <ExclamationCircleOutlined className={classes.rejected} size={12} width='fit-content' />
    </Popover>
  ),
};

type Props = {
  date: string;
  chatMessage: IChatMessage;
  isTagsDisabled: boolean;
  contextMenu: (message: IChatMessage) => MenuProps['items'];
  replyOn: (chatMessage: IChatMessage, isRecieve: boolean) => JSX.Element;
  t: TFunction;
  onTagClick: (event: any) => void;
  currentUserEmail?: string;
  aiAssistant?: boolean;
};

const SendMessage: React.FC<Props> = ({
  date,
  contextMenu,
  replyOn,
  chatMessage,
  t,
  isTagsDisabled,
  onTagClick,
  currentUserEmail,
  aiAssistant,
}) => {
  const { checkingMessages, attemptCount, messageForReply } = useAppSelector((state) => state.chat);
  const dispatch = useAppDispatch();
  const isMentioned = currentUserEmail ? checkUserMentioned(chatMessage.message, currentUserEmail) : false;
  
  const onResend = () => {
    dispatch(
      setChatMessage({
        attachment_ids: [],
        message: chatMessage.message,
        id: chatMessage.created_at!,
        status: 'pending',
        replied_to: messageForReply ? messageForReply : null,
      })
    );
    dispatch(setAttemptCount(attemptCount + 1));
    dispatch(
      fetchChatData({
        data: {
          ...chatMessage,
          status: 'pending',
        },
        type: 'replace',
        status: 'pending',
      })
    );
    dispatch(setChatStatus('send'));
    dispatch(setCheckingMessages(checkingMessages.filter((checkMessages) => checkMessages.id !== chatMessage.id)));
  };

  const onCancel = () => {
    dispatch(fetchChatData({ data: chatMessage, type: 'cancel' }));
    dispatch(setCheckingMessages(checkingMessages.filter((checkMessages) => checkMessages.id !== chatMessage.id)));
  };
  return (
    <Dropdown
      classNames={{ root: classes.dropdown }}
      menu={{ items: chatMessage.status === 'pending' ? [] : contextMenu(chatMessage) }}
      trigger={['contextMenu']}
    >
      <div className={classes.sendWrapper}>
        <div
          className={cn(classes.sendSpace, {
            [classes.aiType]: aiAssistant
          })}
        >
          <div
            id={chatMessage.id}
            className={cn(
              classes.send,
              classes.first,
              chatMessage.status === 'pending' && classes.loading,
              isMentioned && classes.mentionedMessage
            )}
          >
            {chatMessage.replied_to && replyOn(chatMessage, false)}

            <div className={classes.messageText}>
              {Boolean(chatMessage.attachment_ids?.length) && (
                <div style={{ position: 'relative' }}>
                  <Image.PreviewGroup
                    preview={{
                      countRender: (current, total) => null,
                    }}
                  >
                    <Carousel style={{ width: '250px', overflow: 'hidden' }}>
                      {chatMessage?.attachment_ids!.map((id, index) => (
                        <Image
                          src={id}
                          fallback={fallbackHolder}
                          key={`Image-${index}`}
                          alt='Attachment'
                          classNames={{ root: classes.attachment_img }}
                        />
                      ))}
                    </Carousel>
                  </Image.PreviewGroup>
                </div>
              )}
              {parsedChatMessages(
                chatMessage.message,
                { className: classes.messageTag, disabled: isTagsDisabled, onClick: onTagClick },
                isTagsDisabled ? t('Documents.table.noPermissionToView') : undefined,
                classes.urlLink,
                classes.userMessageTag
              )}
            </div>
            <div className={classes.rightInfo}>
              <div className={classes.timeElemSend}>{date}</div>
              {statusCases[chatMessage.status]({
                popupContent: (
                  <div>
                    <Button className={classes.reject_buttons} onClick={onResend} icon={<ReloadOutlined />}>
                      Отправить повторно
                    </Button>
                    <br />
                    <Button className={classes.reject_buttons} onClick={onCancel} icon={<DeleteOutlined />}>
                      Отменить отправку
                    </Button>
                  </div>
                ),
              })}
            </div>
          </div>
        </div>
        {!aiAssistant && <Avatar size={34} className={classes.avatar}>
          <span className={classes.avatarWord}>
            {chatMessage.user_name
              .split(/\s+/)
              .map((t) => t[0])
              .join('')}
          </span>
        </Avatar>}
      </div>
    </Dropdown>
  );
};

export default SendMessage;
